import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "menu", "selectedList", "hiddenInputs"]
  static values = {
    tools: Array,
    selected: Array,
    inputName: String
  }

  connect() {
    this.renderSelected()
    this.renderMenu()
    document.addEventListener("click", this.closeIfOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.closeIfOutside)
  }

  closeIfOutside = (event) => {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  focus() {
    this.renderMenu()
    this.open()
  }

  filter() {
    this.renderMenu()
    this.open()
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  select(event) {
    event.preventDefault()

    const key = event.currentTarget.dataset.toolKey
    const tool = this.toolsValue.find((item) => item.key === key)
    if (!tool) return

    this.selectedValue = [...this.selectedValue, tool]
    this.inputTarget.value = ""
    this.renderSelected()
    this.renderMenu()
    this.inputTarget.focus()
  }

  remove(event) {
    event.preventDefault()

    const key = event.currentTarget.dataset.toolKey
    this.selectedValue = this.selectedValue.filter((tool) => tool.key !== key)
    this.renderSelected()
    this.renderMenu()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
  }

  close() {
    this.menuTarget.classList.add("hidden")
  }

  renderSelected() {
    this.selectedListTarget.innerHTML = this.selectedValue.map((tool) => `
      <span class="inline-flex max-w-full items-center gap-1.5 rounded-full bg-[var(--color-paper-sunken)] px-2.5 py-1 text-xs font-medium text-[var(--color-ink)]">
        <span class="truncate">${this.escapeHtml(tool.name)}</span>
        <button type="button"
                class="inline-flex h-4 w-4 items-center justify-center rounded-full text-[var(--color-ink-tertiary)] hover:bg-[var(--color-border)] hover:text-[var(--color-ink)]"
                data-action="tool-picker#remove"
                data-tool-key="${this.escapeHtml(tool.key)}"
                aria-label="Remove ${this.escapeHtml(tool.name)}">
          <span aria-hidden="true">&times;</span>
        </button>
      </span>
    `).join("")

    this.hiddenInputsTarget.innerHTML = `
      <input type="hidden" name="${this.escapeHtml(this.inputNameValue)}" value="">
      ${this.selectedValue.map((tool) => `
        <input type="hidden" name="${this.escapeHtml(this.inputNameValue)}" value="${this.escapeHtml(tool.key)}">
      `).join("")}
    `
  }

  renderMenu() {
    const query = this.inputTarget.value.toLowerCase().trim()
    const selectedKeys = new Set(this.selectedValue.map((tool) => tool.key))
    const options = this.toolsValue.filter((tool) => {
      if (selectedKeys.has(tool.key)) return false
      if (query.length === 0) return true

      return [
        tool.name,
        tool.description,
        tool.integration,
        tool.key
      ].some((value) => value?.toLowerCase().includes(query))
    })

    if (options.length === 0) {
      this.menuTarget.innerHTML = `
        <div class="px-3 py-2 text-sm text-[var(--color-ink-tertiary)]">
          No tools found
        </div>
      `
      return
    }

    this.menuTarget.innerHTML = options.map((tool) => `
      <button type="button"
              class="block w-full px-3 py-2 text-left hover:bg-[var(--color-paper-sunken)]"
              data-action="tool-picker#select"
              data-tool-key="${this.escapeHtml(tool.key)}">
        <span class="block text-sm font-medium text-[var(--color-ink)]">${this.escapeHtml(tool.name)}</span>
        <span class="mt-0.5 block text-xs text-[var(--color-ink-tertiary)]">${this.escapeHtml(tool.integration || "")}</span>
        ${tool.description ? `<span class="mt-1 block text-xs leading-5 text-[var(--color-ink-tertiary)]">${this.escapeHtml(this.truncate(tool.description, 120))}</span>` : ""}
      </button>
    `).join("")
  }

  truncate(text, length) {
    if (text.length <= length) return text

    return `${text.slice(0, length - 1)}...`
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text ?? ""
    return div.innerHTML
  }
}
